import React from 'react';
import ErrorBoundary from './ErrorBoundary';
import { motion } from 'framer-motion';
import { AlertTriangle, RefreshCw } from 'lucide-react';

// Dashboard-specific error boundary
export const DashboardErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dashboardFallback = (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <motion.div
        className="glass-card p-8 text-center max-w-md w-full"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-8 h-8 text-red-400" />
        </div>
        
        <h1 className="text-2xl font-bold text-white mb-2">Dashboard Error</h1>
        <p className="text-gray-400 mb-6">
          There was an error loading the dashboard. Please refresh the page to try again.
        </p>

        <button
          onClick={() => window.location.reload()}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-accent hover:bg-accent/80 text-white rounded-lg transition-colors mx-auto"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh Dashboard
        </button>
      </motion.div>
    </div>
  );

  return (
    <ErrorBoundary fallback={dashboardFallback}>
      {children}
    </ErrorBoundary>
  );
};

// Chart-specific error boundary
export const ChartErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const chartFallback = (
    <div className="glass-card p-6 border border-red-500/20">
      <div className="flex items-center gap-3 mb-4">
        <AlertTriangle className="w-5 h-5 text-red-400" />
        <h3 className="text-white font-semibold">Chart Error</h3>
      </div>
      <p className="text-gray-400 text-sm mb-4">
        Unable to display this chart. The data may be unavailable or corrupted.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="text-xs text-accent hover:text-accent/80 transition-colors"
      >
        Try again
      </button>
    </div>
  );

  return (
    <ErrorBoundary fallback={chartFallback}>
      {children}
    </ErrorBoundary>
  );
};

// Component-specific error boundary
export const ComponentErrorBoundary: React.FC<{ 
  children: React.ReactNode;
  componentName?: string;
}> = ({ children, componentName = 'Component' }) => {
  const componentFallback = (
    <div className="glass-card p-4 border border-red-500/20">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-4 h-4 text-red-400" />
        <span className="text-white text-sm font-medium">{componentName} Error</span>
      </div>
      <p className="text-gray-400 text-xs">
        This component encountered an error and cannot be displayed.
      </p>
    </div>
  );

  return (
    <ErrorBoundary fallback={componentFallback}>
      {children}
    </ErrorBoundary>
  );
};

// Admin-specific error boundary
export const AdminErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const adminFallback = (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <motion.div
        className="glass-card p-8 text-center max-w-md w-full"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-8 h-8 text-red-400" />
        </div>
        
        <h1 className="text-2xl font-bold text-white mb-2">Admin Panel Error</h1>
        <p className="text-gray-400 mb-6">
          There was an error in the admin panel. Please refresh the page or contact support.
        </p>

        <div className="flex gap-3 justify-center">
          <button
            onClick={() => window.location.reload()}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-accent hover:bg-accent/80 text-white rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          
          <button
            onClick={() => window.location.href = '/admin'}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
          >
            Back to Admin
          </button>
        </div>
      </motion.div>
    </div>
  );

  return (
    <ErrorBoundary fallback={adminFallback}>
      {children}
    </ErrorBoundary>
  );
};

// Authentication-specific error boundary
export const AuthErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const authFallback = (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <motion.div
        className="glass-card p-8 text-center max-w-md w-full"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-8 h-8 text-red-400" />
        </div>
        
        <h1 className="text-2xl font-bold text-white mb-2">Authentication Error</h1>
        <p className="text-gray-400 mb-6">
          There was an error with the authentication system. Please try logging in again.
        </p>

        <button
          onClick={() => window.location.href = '/'}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-accent hover:bg-accent/80 text-white rounded-lg transition-colors mx-auto"
        >
          Return to Login
        </button>
      </motion.div>
    </div>
  );

  return (
    <ErrorBoundary fallback={authFallback}>
      {children}
    </ErrorBoundary>
  );
};
